import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/layout/PublicLayout';
import ContestsPage from './ContestsPage';
import Footer from '../../components/layout/Footer';
import { useTheme } from '../../context/ThemeContext';

// ── Intersection observer hook ─────────────────────────────────────────────────
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

// ── Animated number counter ────────────────────────────────────────────────────
const Counter = ({ target, suffix = '' }) => {
  const [val, setVal] = useState(0);
  const [ref, inView] = useInView(0.5);
  useEffect(() => {
    if (!inView) return;
    let current = 0;
    const step = target / 60;
    const t = setInterval(() => {
      current += step;
      if (current >= target) { setVal(target); clearInterval(t); }
      else setVal(Math.floor(current));
    }, 16);
    return () => clearInterval(t);
  }, [inView, target]);

  const display = val >= 1000000
    ? (val / 1000000).toFixed(0) + 'M'
    : val >= 1000
    ? (val / 1000).toFixed(0) + 'K'
    : val;

  return <span ref={ref}>{display}{suffix}</span>;
};

// ── Typewriter ─────────────────────────────────────────────────────────────────
const Typewriter = () => {
  const words = ['Contests', 'Elections', 'Awards', 'Competitions', 'Polls'];
  const [wi, setWi] = useState(0);
  const [ci, setCi] = useState(0);
  const [del, setDel] = useState(false);

  useEffect(() => {
    const word = words[wi];
    const t = setTimeout(() => {
      if (!del && ci < word.length) { setCi(c => c + 1); }
      else if (!del && ci === word.length) { setTimeout(() => setDel(true), 1600); }
      else if (del && ci > 0) { setCi(c => c - 1); }
      else if (del && ci === 0) { setDel(false); setWi(i => (i + 1) % words.length); }
    }, del ? 45 : 90);
    return () => clearTimeout(t);
  }, [ci, del, wi]);

  return <>{words[wi].slice(0, ci)}<span className="opacity-60">|</span></>;
};

// ── Main ───────────────────────────────────────────────────────────────────────
const HomePage = () => {
  // ✅ Read theme from context — no local state needed
  const { dark } = useTheme();
  const d = dark;

  const [featRef, featInView] = useInView();
  const [howRef, howInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');
        .hp * { font-family: 'Plus Jakarta Sans', sans-serif !important; }

        @keyframes hp-up    { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes hp-down  { from{opacity:0;transform:translateY(-24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes hp-left  { from{opacity:0;transform:translateX(-28px)} to{opacity:1;transform:translateX(0)} }
        @keyframes hp-right { from{opacity:0;transform:translateX(28px)} to{opacity:1;transform:translateX(0)} }
        @keyframes hp-pop   { from{opacity:0;transform:scale(0.9)} to{opacity:1;transform:scale(1)} }
        @keyframes hp-card  { from{opacity:0;transform:translateY(36px)} to{opacity:1;transform:translateY(0)} }
        @keyframes hp-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes hp-spin  { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes hp-glow  { 0%,100%{opacity:.4} 50%{opacity:.8} }
        @keyframes hp-grad  { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes hp-marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes hp-pulse-ring { 0%{transform:scale(1);opacity:.5} 100%{transform:scale(2);opacity:0} }

        .hp .au   { animation: hp-up    .7s cubic-bezier(.22,1,.36,1) forwards; }
        .hp .ad   { animation: hp-down  .6s cubic-bezier(.22,1,.36,1) forwards; }
        .hp .al   { animation: hp-left  .7s cubic-bezier(.22,1,.36,1) forwards; }
        .hp .ar   { animation: hp-right .7s cubic-bezier(.22,1,.36,1) forwards; }
        .hp .ap   { animation: hp-pop   .65s cubic-bezier(.34,1.56,.64,1) forwards; }
        .hp .ac   { animation: hp-card  .65s cubic-bezier(.22,1,.36,1) forwards; }
        .hp .af   { animation: hp-float 4s ease-in-out infinite; }
        .hp .as   { animation: hp-spin  18s linear infinite; }
        .hp .ag   { animation: hp-glow  2.5s ease-in-out infinite; }

        .hp .d1 { animation-delay:.1s; opacity:0 }
        .hp .d2 { animation-delay:.2s; opacity:0 }
        .hp .d3 { animation-delay:.3s; opacity:0 }
        .hp .d4 { animation-delay:.4s; opacity:0 }
        .hp .d5 { animation-delay:.5s; opacity:0 }
        .hp .d6 { animation-delay:.6s; opacity:0 }
        .hp .d7 { animation-delay:.7s; opacity:0 }
        .hp .d8 { animation-delay:.8s; opacity:0 }

        .hp .grad-text {
          background: linear-gradient(135deg, #ef4444 0%, #f97316 30%, #ef4444 60%, #dc2626 100%);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: hp-grad 4s ease infinite;
        }

        .hp .btn-primary {
          background: linear-gradient(135deg, #ef4444, #dc2626, #b91c1c);
          background-size: 200% 200%;
          animation: hp-grad 3s ease infinite;
          color: white;
          font-weight: 800;
          border-radius: 12px;
          padding: 14px 32px;
          font-size: 15px;
          transition: transform .2s, box-shadow .2s;
          box-shadow: 0 8px 32px rgba(239,68,68,.35);
          display: inline-block;
        }
        .hp .btn-primary:hover {
          transform: translateY(-3px) scale(1.03);
          box-shadow: 0 20px 48px rgba(239,68,68,.5);
        }
        .hp .btn-primary:active { transform: scale(.96); }

        .hp .btn-ghost {
          font-weight: 700;
          border-radius: 12px;
          padding: 14px 32px;
          font-size: 15px;
          transition: all .2s;
          border: 2px solid;
          display: inline-block;
        }
        .hp .btn-ghost:hover { transform: translateY(-3px); }
        .hp .btn-ghost:active { transform: scale(.96); }

        .hp .feat-card {
          transition: all .35s cubic-bezier(.22,1,.36,1);
          position: relative;
          overflow: hidden;
        }
        .hp .feat-card:hover { transform: translateY(-8px); }
        .hp .feat-card::before {
          content:'';
          position:absolute;
          top:0; left:0; right:0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #ef4444, transparent);
          opacity: 0;
          transition: opacity .3s;
        }
        .hp .feat-card:hover::before { opacity: 1; }

        .hp .marquee-track {
          display: flex;
          width: max-content;
          animation: hp-marquee 24s linear infinite;
        }
        .hp .marquee-track:hover { animation-play-state: paused; }

        .hp .p-ring {
          position: absolute;
          inset: -6px;
          border-radius: 50%;
          border: 2px solid rgba(239,68,68,.5);
          animation: hp-pulse-ring 2.2s ease-out infinite;
        }
        .hp .p-ring-2 { animation-delay: 1.1s; }

        .hp .ghost-num {
          position: absolute;
          top: -16px;
          left: -8px;
          font-size: 96px;
          font-weight: 900;
          line-height: 1;
          background: linear-gradient(180deg, #ef4444 0%, transparent 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          opacity: .1;
          pointer-events: none;
          user-select: none;
        }

        .hp .grid-bg {
          background-image: linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px);
          background-size: 64px 64px;
        }
        .hp .grid-bg-light {
          background-image: linear-gradient(rgba(0,0,0,.04) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(0,0,0,.04) 1px, transparent 1px);
          background-size: 64px 64px;
        }

        .hp .pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 14px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .1em;
          text-transform: uppercase;
        }

        .hp .clip-both { clip-path: polygon(0 4%, 100% 0, 100% 96%, 0 100%); }
      `}</style>

      <div className={`hp transition-colors duration-500 ${d ? 'bg-gray-950 text-white' : 'bg-white text-gray-900'}`}>
        <PublicLayout>

          {/* ══════════════════════════════════════
              HERO
          ══════════════════════════════════════ */}
          <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
            <div className="absolute inset-0" style={{
              background: d
                ? 'radial-gradient(ellipse 90% 55% at 50% -5%, rgba(239,68,68,.22) 0%, transparent 60%), #030712'
                : 'radial-gradient(ellipse 90% 55% at 50% -5%, rgba(239,68,68,.12) 0%, transparent 60%), #ffffff',
            }}/>
            <div className={`absolute inset-0 ${d ? 'grid-bg' : 'grid-bg-light'}`}/>

            <div className="absolute top-24 left-6 w-80 h-80 rounded-full blur-3xl af pointer-events-none"
              style={{ background:'radial-gradient(circle, rgba(239,68,68,.14), transparent 70%)' }}/>
            <div className="absolute bottom-28 right-6 w-64 h-64 rounded-full blur-3xl af pointer-events-none"
              style={{ background:'radial-gradient(circle, rgba(239,68,68,.10), transparent 70%)', animationDelay:'2s' }}/>

            <div className="absolute right-12 top-24 w-28 h-28 as opacity-20 pointer-events-none">
              <svg viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="46" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="7 5"/></svg>
            </div>
            <div className="absolute left-20 bottom-36 w-20 h-20 as opacity-15 pointer-events-none"
              style={{ animationDirection:'reverse', animationDuration:'10s' }}>
              <svg viewBox="0 0 80 80" fill="none"><circle cx="40" cy="40" r="36" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="6 4"/></svg>
            </div>

            <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 pb-12 w-full">
              {/* <div className={`ad d1 pill mb-7 ${d ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block"/>
                Secure · Live · Transparent
              </div> */}

              <h1 className="au d2 text-6xl sm:text-7xl md:text-[88px] font-black leading-[1.0] tracking-tight mb-5">
                <span className="grad-text">eBallot</span>
                <br/>
                <span className={d ? 'text-white' : 'text-gray-900'}>Box</span>
              </h1>

              <p className={`au d3 text-xl sm:text-2xl font-semibold mb-4 ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                The #1 platform for digital <span className="grad-text"><Typewriter /></span>
              </p>

              <p className={`au d4 text-base sm:text-lg max-w-xl mx-auto mb-10 leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                Host contests, run elections, and manage awards with live analytics, secure payments, and SMS notifications — all in one place.
              </p>

              <div className="au d5 flex flex-col sm:flex-row gap-3 justify-center items-center mb-10">
                <Link to="/contests"><span className="btn-primary">Browse Contests</span></Link>
                <Link to="/elections">
                  <span className={`btn-ghost ${d ? 'border-white/15 text-white hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                    🗳️ Election Hub
                  </span>
                </Link>
                <Link to="/register">
                  <span className={`btn-ghost ${d ? 'border-white/15 text-white hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                    Get Started →
                  </span>
                </Link>
              </div>

              <div className="au d6 flex items-center justify-center gap-3 mb-14">
                <div className="flex -space-x-2">
                  {['#ef4444','#f97316','#eab308','#22c55e','#3b82f6'].map((c,i) => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{ background:c, borderColor: d ? '#030712' : '#fff', zIndex: 5-i }}>
                      {String.fromCharCode(65+i)}
                    </div>
                  ))}
                </div>
                <p className={`text-sm font-semibold ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="text-red-500 font-bold">500+</span> orgs already using eBallot Box
                </p>
              </div>
            </div>

            {/* Stats bar */}
            <div className="relative z-10 w-full max-w-3xl mx-auto px-4 sm:px-6 mb-10">
              <div className={`rounded-2xl grid grid-cols-3 divide-x overflow-hidden ${d ? 'bg-white/5 backdrop-blur-md border border-white/10 divide-white/10' : 'bg-gray-50 border border-gray-200 divide-gray-200 shadow-sm'}`}>
                {[
                  { target:500, suffix:'+', label:'Organizations' },
                  { target:1000000, suffix:'', label:'Votes Cast' },
                  { target:99, suffix:'.9%', label:'Uptime' },
                ].map((s,i) => (
                  <div key={i} className="py-5 text-center">
                    <div className="text-2xl sm:text-3xl font-black text-red-500 mb-0.5">
                      <Counter target={s.target} suffix={s.suffix}/>
                    </div>
                    <div className={`text-xs font-bold tracking-widest uppercase ${d ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 animate-bounce">
              <div className={`w-5 h-8 rounded-full border-2 flex justify-center pt-1 ${d ? 'border-white/20' : 'border-gray-300'}`}>
                <div className="w-0.5 h-2 rounded-full bg-red-500"/>
              </div>
            </div>
          </section>

          <ContestsPage />

          {/* ══════════════════════════════════════
              FEATURES
          ══════════════════════════════════════ */}
          <section ref={featRef} className={`py-28 clip-both transition-colors duration-500 ${d ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`text-center mb-16 ${featInView ? 'au' : 'opacity-0'}`}>
                <span className={`pill mb-4 ${d ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>Features</span>
                <h2 className={`text-4xl sm:text-5xl font-black mt-4 mb-3 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Everything you need,<br/><span className="grad-text">nothing you don't</span>
                </h2>
                <p className={`text-lg max-w-lg mx-auto ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                  Built specifically for Ghana's contest & voting market
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[
                  { emoji:'🔐', title:'Bank-Level Security', desc:'End-to-end encrypted. Every vote tamper-proof.', d:'d1' },
                  { emoji:'⚡', title:'Lightning Fast', desc:'Sub-second processing. Thousands of votes per minute.', d:'d2' },
                  { emoji:'📊', title:'Live Analytics', desc:'Watch votes roll in live with real-time leaderboards.', d:'d3' },
                  { emoji:'📱', title:'Mobile First', desc:'Flawless on every phone. Vote anywhere, anytime.', d:'d4' },
                  { emoji:'💳', title:'Paystack + MoMo', desc:'Cards, mobile money, and bank transfers all supported.', d:'d5' },
                  { emoji:'🔔', title:'SMS Notifications', desc:'Instant alerts to voters and contestants via mNotify.', d:'d6' },
                ].map((f,i) => (
                  <div key={i} className={`feat-card p-6 rounded-2xl border ${featInView ? `ac ${f.d}` : 'opacity-0'} ${d ? 'bg-gray-800/50 border-white/6 hover:border-red-500/30' : 'bg-white border-gray-100 hover:border-red-200 shadow-sm hover:shadow-lg'}`}>
                    <div className="text-3xl mb-4 inline-block af" style={{ animationDelay:`${i*0.3}s` }}>{f.emoji}</div>
                    <h3 className={`text-base font-bold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>{f.title}</h3>
                    <p className={`text-sm leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════
              HOW IT WORKS
          ══════════════════════════════════════ */}
          <section ref={howRef} className={`py-28 transition-colors duration-500 ${d ? 'bg-gray-950' : 'bg-white'}`}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`text-center mb-20 ${howInView ? 'au' : 'opacity-0'}`}>
                <span className={`pill mb-4 ${d ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>How It Works</span>
                <h2 className={`text-4xl sm:text-5xl font-black mt-4 mb-3 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Live in <span className="grad-text">3 steps</span>
                </h2>
                <p className={`text-lg ${d ? 'text-gray-400' : 'text-gray-500'}`}>No technical knowledge required</p>
              </div>

              <div className="relative">
                <div className="hidden md:block absolute top-11 left-[20%] right-[20%] h-px"
                  style={{ background:'linear-gradient(90deg, transparent, #ef4444 30%, #ef4444 70%, transparent)' }}/>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8">
                  {[
                    { num:'01', title:'Register', desc:"Create your organization account in 2 minutes. No credit card needed.", anim:'al d1' },
                    { num:'02', title:'Setup Contest', desc:"Add categories, upload photos, set your vote price — done.", anim:'au d3' },
                    { num:'03', title:'Go Live', desc:"Share your link. Watch votes and payments roll in live.", anim:'ar d5' },
                  ].map((step,i) => (
                    <div key={i} className={`relative text-center ${howInView ? step.anim : 'opacity-0'}`}>
                      <div className="ghost-num">{step.num}</div>
                      <div className="relative w-20 h-20 mx-auto mb-6">
                        <div className="p-ring ag"/>
                        <div className="p-ring p-ring-2"/>
                        <div className="relative w-full h-full rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-xl shadow-red-600/25 text-white text-xl font-black z-10">
                          {step.num}
                        </div>
                      </div>
                      <h3 className={`text-xl font-bold mb-3 ${d ? 'text-white' : 'text-gray-900'}`}>{step.title}</h3>
                      <p className={`text-sm leading-relaxed max-w-xs mx-auto ${d ? 'text-gray-400' : 'text-gray-500'}`}>{step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════
              CTA
          ══════════════════════════════════════ */}
          <section ref={ctaRef} className="relative py-28 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-red-950"/>
            <div className="absolute inset-0" style={{
              backgroundImage:`radial-gradient(circle at 2px 2px, rgba(255,255,255,.06) 1px, transparent 0)`,
              backgroundSize:'32px 32px',
            }}/>
            <div className="absolute top-0 left-1/3 w-80 h-80 rounded-full blur-3xl opacity-25 pointer-events-none"
              style={{ background:'radial-gradient(circle, rgba(255,255,255,.5), transparent)' }}/>
            <div className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
              style={{ background:'radial-gradient(circle, rgba(0,0,0,.6), transparent)' }}/>
            <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-52 h-52 as opacity-10 pointer-events-none">
              <svg viewBox="0 0 200 200" fill="none"><circle cx="100" cy="100" r="95" stroke="white" strokeWidth="1.5" strokeDasharray="10 7"/></svg>
            </div>

            <div className={`relative z-10 text-center max-w-3xl mx-auto px-4 ${ctaInView ? 'ap' : 'opacity-0'}`}>
              <span className="pill mb-6 bg-white/15 text-white border border-white/25">🚀 Start Free Today</span>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-5 leading-[1.1]">
                Run your next big<br/>contest with confidence
              </h2>
              <p className="text-lg text-red-100 mb-10 max-w-xl mx-auto leading-relaxed">
                Join hundreds of organizations using eBallot Box for secure, transparent, and beautiful voting experiences.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                <Link to="/register">
                  <span className="inline-block px-10 py-4 bg-white text-red-600 font-black text-base rounded-xl shadow-2xl hover:scale-105 hover:-translate-y-1 active:scale-95 transition-all duration-200">
                    Create Free Account →
                  </span>
                </Link>
                <Link to="/contests">
                  <span className="inline-block px-10 py-4 bg-white/10 backdrop-blur text-white font-bold text-base rounded-xl border border-white/25 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 active:scale-95 transition-all duration-200">
                    Browse Contests
                  </span>
                </Link>
              </div>
              <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-red-100">
                {['✓ Free to start','✓ No credit card required','✓ 24/7 support'].map((t,i) => (
                  <span key={i} className="text-sm font-semibold">{t}</span>
                ))}
              </div>
            </div>
          </section>

          <Footer />
        </PublicLayout>
      </div>
    </>
  );
};

export default HomePage;